<template>
    <RegularModal ref="modal" class="brackets-default-configuration-modal" modal-card-class="is-medium">
        <template v-slot:title>
            {{ translate("admin_bracket_listing.defaults_configuration") }}
        </template>
        <template v-slot:body>
            <div class="columns is-mobile" v-if="initDone">
                <div class="column">
                    <p class="mb-3 is-size-5">
                        {{ translate("admin_bracket_listing.what_competitors_to_exclude_title") }}
                    </p>

                    <div class="field">
                        <label class="label">
                            {{ translate("admin_bracket_listing.what_competitors_to_exclude_subtitle") }}
                        </label>
                        <div class="control">
                            <div class="select">
                                <select v-model="competitorsExcludeStrategy">
                                    <option :value="CompetitorsExcludeStrategy.ExcludeMissingPayments">
                                        {{ translate("admin_bracket_listing.exclude_missing_payments") }}
                                    </option>
                                    <option :value="CompetitorsExcludeStrategy.ExcludeMissingPaymentsAndDisqualified">
                                        {{ translate("admin_bracket_listing.exclude_missing_payments_and_disqualifications") }}
                                    </option>
                                    <option :value="CompetitorsExcludeStrategy.NoExclusion">
                                        {{ translate("admin_bracket_listing.no_exclusions_of_competitors_to_put_to_bracket") }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="field mt-4">
                        <Checkbox2 v-model="enableBracketsWithSingleCompetitor">
                            {{ translate("event_setting.enableBracketsWithSingleCompetitor") }}
                        </Checkbox2>
                    </div>
                </div>
            </div>
            <div v-else class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton class="button is-primary" @click="confirm" :is-loading="confirmInProgress">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { ref } from "vue";
    import bracketsApi, {
        BracketDefaultsConfigurationResponse,
        CompetitorsExcludeStrategy,
        CompetitorsExcludeStrategyType,
    } from "../../Api/brackets";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { BracketTypeConstraint } from "../configurationTemplates";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import { translate } from "../../Common/translations";
    import { successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";
    import { Modal } from "../../Common/useModal";
    import { useBracketsListingStore } from "./bracketsListingApp";

    const props = defineProps<{
        eventId: number;
    }>();
    const modal = ref<Modal | null>(null);
    const competitorsExcludeStrategy = ref<CompetitorsExcludeStrategyType>(CompetitorsExcludeStrategy.ExcludeMissingPayments);
    const bracketTypeConstraints = ref<BracketTypeConstraint[]>([]);
    const enableBracketsWithSingleCompetitor = ref(false);
    const initDone = ref(false);

    const store = useBracketsListingStore();
    const confirmInProgress = ref(false);

    function confirm() {
        confirmInProgress.value = true;

        return bracketsApi
            .updateBracketDefaultsConfiguration(props.eventId, {
                bracketTypeConstraints: bracketTypeConstraints.value,
                configurationOptions: {
                    competitorsExcludeStrategy: competitorsExcludeStrategy.value,
                    enableBracketsWithSingleCompetitor: enableBracketsWithSingleCompetitor.value,
                },
            })
            .then(() => {
                successToast(translate("admin_bracket_listing.brackets_configuration_updated"));
                close();

                return store.refreshBracketsListing();
            })
            .catch(handleError)
            .finally(() => {
                confirmInProgress.value = false;
            });
    }

    function open() {
        initDone.value = false;
        modal.value?.open();

        return bracketsApi
            .getBracketDefaultsConfiguration(props.eventId)
            .then((defaultsResponse: BracketDefaultsConfigurationResponse) => {
                bracketTypeConstraints.value = defaultsResponse.bracketTypeConstraints;
                competitorsExcludeStrategy.value = defaultsResponse.configurationOptions.competitorsExcludeStrategy;
                enableBracketsWithSingleCompetitor.value = defaultsResponse.configurationOptions.enableBracketsWithSingleCompetitor;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function close() {
        modal.value?.close();
    }

    defineExpose({ open, close });
</script>

<style scoped></style>
